import React, { useEffect, useMemo, useRef, useState } from 'react'

import AssessmentIcon from '@mui/icons-material/Assessment'
import DownloadIcon from '@mui/icons-material/Download'
import ImageIcon from '@mui/icons-material/Image'
import SearchIcon from '@mui/icons-material/Search'
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    InputLabel,
    ListItemText,
    ListSubheader,
    MenuItem,
    OutlinedInput,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Typography,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import { format, startOfMonth } from 'date-fns'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'

import { ifAnyGranted } from '../auth'
import { MultipleSelectUnidade, MultipleSelectUnidadeBasic } from '../component/multipleSelectCheck'
import { useExport } from '../hooks/useExport'
import AuthService from '../service'
import { gerarRelatorio, lerMensagemDeErro, listarDatasets } from '../service/reportService'
import AiPrompt from './AiPrompt'
import GraficoRelatorio from './GraficoRelatorio'
import {
    MAX_COLUNAS_AGRUPAMENTO,
    MAX_METRICAS,
    colunasPadrao,
    colunasParaAgrupar,
    dicaSomarPor,
    metricasPadrao,
    nomeMetrica,
    opcoesDeMetrica,
    ordemPadrao,
    ordenadoPelaConta,
    podeAgrupar,
    rotuloNoSeletor,
    rotuloSaida,
    temMetrica,
    textoDoTeto,
    validarLimite,
} from './agrupamentoUi'
import { baixarGraficoPng, nomeArquivoGrafico } from './baixarGraficoPng'
import { legendaParaImagem, montarDadosGrafico, tituloParaImagem } from './dadosGrafico'
import { montarExportOptions } from './exportOptions'
import { formatarCelula, formatarCelulaDoGrupo } from './formatarCelula'
import { chavesDosIds, idsDasChaves, rotuloSelecao, unidadesIniciais } from './unidadesUi'

// Quantas linhas aparecem na tela. O CSV leva tudo o que o backend devolveu.
const LINHAS_NA_PREVIA = 100

// Como cada operador de filtro aparece na tela, em português de gente.
const TEXTO_OPERADOR = {
    igual: 'é',
    diferente: 'não é',
    contem: 'contém',
    maior: 'maior que',
    menor: 'menor que',
}

const AJUDA_LOJAS = 'Mesmos dados e mesmas lojas do painel do Analytics. Mude aqui só para este relatório.'

function hoje() {
    return format(new Date(), 'yyyy-MM-dd')
}

// Título de cada passo: número num círculo e o nome, alinhado à esquerda (o
// tema do painel centraliza texto por padrão, e formulário centralizado se lê mal).
function TituloDoPasso({ numero, titulo, ajuda }) {
    return (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 15, fontWeight: 600 }}>
                {numero}
            </Avatar>
            <Box>
                <Typography variant="subtitle1" component="h3" align="left" sx={{ lineHeight: 1.2 }}>
                    {titulo}
                </Typography>
                {!!ajuda && (
                    <Typography variant="body2" color="text.secondary" align="left">
                        {ajuda}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

function primeiroDiaDoMes() {
    return format(startOfMonth(new Date()), 'yyyy-MM-dd')
}

export default function Report() {
    const { enqueueSnackbar } = useSnackbar()
    const { handleExport } = useExport()

    const perfil = AuthService.profile()
    const temAnalyticsPro = ifAnyGranted('ROLE_SISCHEF_ANALYTICS_PRO')
    // Montar por frase é o mesmo recurso do consultor de IA: mesma permissão.
    const temChatIa = ifAnyGranted('PERMITE_ACESSAR_CHAT_IA')
    const lojas = useMemo(() => {
        if (!temAnalyticsPro) {
            return perfil?.unidade ? [perfil.unidade] : []
        }
        return perfil?.unidadeList || []
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [temAnalyticsPro])
    // As lojas escolhidas ao abrir o Analytics: o relatório começa pelas mesmas.
    const selecaoDoPainel = useSelector(state => state.dashboardFilter?.unidadeSelected)

    const [datasets, setDatasets] = useState([])
    const [datasetId, setDatasetId] = useState('')
    const [colunas, setColunas] = useState([])
    const [inicio, setInicio] = useState(primeiroDiaDoMes())
    const [fim, setFim] = useState(hoje())
    const [unidades, setUnidades] = useState(() => unidadesIniciais(lojas, selecaoDoPainel))
    // Ordenação e filtros só chegam pela IA hoje; o montador manual não os edita.
    const [ordenacao, setOrdenacao] = useState(null)
    const [filtros, setFiltros] = useState([])

    // "Como mostrar": lista completa (padrão) ou totais por uma a três colunas.
    const [modoAgrupado, setModoAgrupado] = useState(false)
    const [agruparPor, setAgruparPor] = useState([])
    const [metricas, setMetricas] = useState([])
    const [limiteTexto, setLimiteTexto] = useState('')
    // Só fica vermelho depois que a pessoa tentou gerar sem escolher.
    const [tentouGerar, setTentouGerar] = useState(false)
    const graficoRef = useRef(null)

    const [carregandoCatalogo, setCarregandoCatalogo] = useState(true)
    const [gerando, setGerando] = useState(false)
    const [erro, setErro] = useState('')
    const [resultado, setResultado] = useState(null)
    // Limite e ordem que geraram o resultado na tela — a legenda fala deles, não do formulário.
    const [geracao, setGeracao] = useState(null)

    const dataset = useMemo(
        () => datasets.find(item => item.id === datasetId) || null,
        [datasets, datasetId],
    )

    const opcoesDeConta = useMemo(() => opcoesDeMetrica(dataset), [dataset])
    const limiteValidado = validarLimite(limiteTexto)
    const grafico = useMemo(() => montarDadosGrafico(resultado, geracao?.ordem), [resultado, geracao])

    useEffect(() => {
        let ativo = true

        listarDatasets()
            .then(resposta => {
                if (!ativo) {
                    return
                }

                const lista = resposta?.datasets || []
                setDatasets(lista)

                if (lista.length) {
                    setDatasetId(lista[0].id)
                    setColunas(colunasPadrao(lista[0]))
                }
            })
            .catch(err => {
                console.error('Erro ao carregar os relatórios:', err)
                if (ativo) {
                    setErro('Não consegui carregar a lista de relatórios. Atualize a página e tente de novo.')
                }
            })
            .finally(() => {
                if (ativo) {
                    setCarregandoCatalogo(false)
                }
            })

        return () => {
            ativo = false
        }
    }, [])

    function handleTrocarDataset(novoId) {
        const novo = datasets.find(item => item.id === novoId)
        setDatasetId(novoId)
        setColunas(novo ? colunasPadrao(novo) : [])
        // Ordenação, filtros e agrupamento apontam para colunas do relatório anterior.
        setOrdenacao(null)
        setFiltros([])
        setModoAgrupado(false)
        setAgruparPor([])
        setMetricas([])
        setLimiteTexto('')
        setResultado(null)
        setErro('')
    }

    function handleLimparOrdenacaoEFiltros() {
        setOrdenacao(null)
        setFiltros([])
        setLimiteTexto('')
    }

    // Rótulo de qualquer coluna que sai no relatório, inclusive as contas do
    // agrupamento — "soma_valorTotal" não pode aparecer na tela.
    function rotuloColuna(nome) {
        return rotuloSaida(nome, dataset)
    }

    function handleTrocarModo(agrupado) {
        setModoAgrupado(agrupado)
        // Ao ligar pela primeira vez já vem marcado o que o dono olha primeiro.
        if (agrupado && !metricas.length) {
            setMetricas(metricasPadrao(dataset))
        }
        // Ordem e limite valiam para o outro formato de linha.
        setOrdenacao(null)
        setLimiteTexto('')
        setTentouGerar(false)
    }

    function handleTrocarAgruparPor(valor) {
        const escolhidas = typeof valor === 'string' ? valor.split(',') : valor
        setAgruparPor(escolhidas)

        if (ordenacao && !escolhidas.includes(ordenacao.coluna) && !temMetrica(metricas, ordenacao.coluna)) {
            setOrdenacao(null)
        }
    }

    // O Select devolve os nomes na ordem em que foram marcados: a primeira vira o gráfico.
    function handleTrocarMetricas(valor) {
        const nomes = typeof valor === 'string' ? valor.split(',') : valor
        const novas = nomes
            .map(nome => opcoesDeConta.find(opcao => opcao.nome === nome))
            .filter(Boolean)
            .map(opcao => (opcao.funcao === 'contagem'
                ? { funcao: 'contagem' }
                : { funcao: opcao.funcao, coluna: opcao.coluna }))
        setMetricas(novas)

        if (ordenacao && !agruparPor.includes(ordenacao.coluna) && !temMetrica(novas, ordenacao.coluna)) {
            // Com "só os maiores" preenchido, a ordem passa para a nova 1ª conta:
            // zerar aqui faria o limite cortar os primeiros com o campo dizendo "maiores".
            setOrdenacao(limiteValidado.limite && novas.length
                ? { coluna: nomeMetrica(novas[0]), direcao: 'desc' }
                : null)
        }
    }

    // Pedir "os maiores" sem ordenar pela conta traria os primeiros da lista. A
    // ordem entra no estado na hora, para o resumo mostrar o que vai acontecer.
    function handleTrocarLimite(texto) {
        setLimiteTexto(texto)

        const { limite: pedido } = validarLimite(texto)
        if (!pedido || !metricas.length || ordenadoPelaConta(ordenacao, metricas)) {
            return
        }

        setOrdenacao({ coluna: nomeMetrica(metricas[0]), direcao: 'desc' })
    }

    function handleAlternarColuna(nome) {
        setColunas(atual => (
            atual.includes(nome) ? atual.filter(item => item !== nome) : [...atual, nome]
        ))
    }

    function handleMarcarTodasColunas() {
        setColunas(dataset ? dataset.colunas.map(c => c.nome) : [])
    }

    function handleLimparColunas() {
        setColunas([])
    }

    // Aplica no montador o que a IA devolveu; o usuário confere antes de gerar.
    function handleAplicarDaIa(estado) {
        const novoDataset = datasets.find(item => item.id === estado.dataset)
        setDatasetId(estado.dataset)

        // Agrupado, estado.colunas é o próprio "agrupar por": as colunas da lista
        // completa ficam guardadas para quando o usuário voltar.
        if (!estado.agrupamento) {
            setColunas(estado.colunas)
        } else if (estado.dataset !== datasetId) {
            setColunas(colunasPadrao(novoDataset))
        }

        if (estado.periodo) {
            setInicio(estado.periodo.inicio)
            setFim(estado.periodo.fim)
        }
        setOrdenacao(estado.ordenacao || null)
        setFiltros(estado.filtros || [])
        setModoAgrupado(!!estado.agrupamento)
        setAgruparPor(estado.agrupamento?.por || [])
        setMetricas(estado.agrupamento?.metricas || [])
        setLimiteTexto(estado.limite ? String(estado.limite) : '')
        setResultado(null)
        setErro('')
    }

    async function handleGerar() {
        setErro('')

        // Sem escolher por onde somar não há o que gerar: mostra onde falta.
        if (modoAgrupado && !agruparPor.length) {
            setTentouGerar(true)
            return
        }

        setGerando(true)

        // Agrupado, a linha é "por + contas": as colunas saem do agrupamento.
        const agrupamento = modoAgrupado ? { por: agruparPor, metricas } : undefined
        // Sem ordem escolhida: com "só os maiores" preenchido vale a 1ª conta, do
        // maior para o menor (senão "maiores" seria mentira); sem limite, o total
        // sai por tempo (do mais antigo) ou pela 1ª conta — nunca na ordem do banco.
        const ordem = ordenacao
            || (agrupamento && limiteValidado.limite && metricas.length
                ? { coluna: nomeMetrica(metricas[0]), direcao: 'desc' }
                : null)
            || (agrupamento ? ordemPadrao(agruparPor, metricas, dataset) : null)

        try {
            const resposta = await gerarRelatorio({
                versao: 1,
                dataset: datasetId,
                colunas: agrupamento ? agrupamento.por : colunas,
                periodo: { inicio, fim },
                unidades,
                // Vão só quando existem: o backend trata ausência como "sem ordem/sem filtro".
                ordenacao: ordem || undefined,
                filtros: filtros.length ? filtros : undefined,
                agrupamento,
                limite: limiteValidado.limite || undefined,
            })
            setResultado(resposta)
            setGeracao({ limite: limiteValidado.limite, ordem })

            if (!resposta?.linhas?.length) {
                enqueueSnackbar('Nenhum resultado para esse período.', { variant: 'info' })
            }
        } catch (err) {
            console.error('Erro ao gerar o relatório:', err)
            setResultado(null)
            setGeracao(null)
            setErro(await lerMensagemDeErro(
                err,
                'Não consegui gerar o relatório. Tente um período menor ou menos lojas.',
            ))
        } finally {
            setGerando(false)
        }
    }

    function handleExportar() {
        if (!resultado?.linhas?.length) {
            return
        }

        handleExport(
            resultado.linhas,
            montarExportOptions(resultado.colunas),
            dataset?.label || 'relatorio',
        )
    }

    async function handleBaixarGrafico() {
        if (!grafico) {
            return
        }

        const area = graficoRef.current
        const svg = area?.querySelector('.recharts-surface') || area?.querySelector('svg')

        try {
            await baixarGraficoPng({
                svg,
                titulo: tituloParaImagem(grafico),
                legenda: legendaParaImagem(grafico),
                nomeArquivo: nomeArquivoGrafico(dataset?.label),
            })
            enqueueSnackbar('Download da imagem iniciado!', { variant: 'success' })
        } catch (err) {
            console.error('Erro ao baixar o gráfico:', err)
            enqueueSnackbar('Não consegui salvar a imagem do gráfico. Tente de novo.', {
                variant: 'error',
            })
        }
    }

    const escolheuOQueMostrar = modoAgrupado ? metricas.length > 0 : colunas.length > 0
    // Campo vazio não quer dizer "todas": a pessoa vê o que vai entrar, e sem loja não gera.
    const faltaLoja = temAnalyticsPro && !unidades.length
    // O limite só é editável no modo somado; no modo lista ele vem da IA, já validado.
    const podeGerar = !!datasetId && escolheuOQueMostrar && (!modoAgrupado || limiteValidado.ok)
        && !!inicio && !!fim && !faltaLoja && !gerando
    const linhasNaTela = resultado?.linhas?.slice(0, LINHAS_NA_PREVIA) || []
    const limite = limiteValidado.limite
    const colunasAgrupadas = resultado?.agrupado ? resultado.agrupamento?.por || [] : []
    const faltaSomarPor = modoAgrupado && tentouGerar && !agruparPor.length
    // No modo somado o campo está à vista; no modo lista, o limite da IA só aparece aqui.
    const limiteNoResumo = !modoAgrupado && !!limite
    const nomesDasContas = metricas.map(nomeMetrica)

    function legendaDoResultado() {
        if (resultado.total > linhasNaTela.length) {
            return `Mostrando ${linhasNaTela.length} de ${resultado.total.toLocaleString('pt-BR')} na tela. O CSV leva tudo.`
        }

        const limiteUsado = geracao?.limite
        if (limiteUsado && resultado.total >= limiteUsado) {
            // "Maiores" só quando o relatório saiu mesmo ordenado por uma conta, do maior para o menor.
            const maiores = !!resultado.agrupado
                && ordenadoPelaConta(geracao?.ordem, resultado.agrupamento?.metricas)
            return `Mostrando só os ${limiteUsado} ${maiores ? 'maiores' : 'primeiros'}, como você pediu.`
        }

        return 'Tudo o que foi encontrado está na tela.'
    }

    return (
        <>
            <Toolbar />

            <Card>
                <CardHeader
                    avatar={<AssessmentIcon color="primary" />}
                    title="Relatórios"
                    subheader="Três passos: escolha o relatório, diga como mostrar e clique em Gerar relatório."
                    titleTypographyProps={{
                        variant: 'h5',
                        component: 'h2',
                        style: { textAlign: 'left' },
                    }}
                />

                <Divider />

                <CardContent>
                    {carregandoCatalogo ? (
                        <Typography>Carregando os relatórios...</Typography>
                    ) : (
                        <>
                            {temChatIa && (
                                <AiPrompt datasets={datasets} onAplicar={handleAplicarDaIa} />
                            )}

                            <TituloDoPasso numero={1} titulo="Escolha o relatório" />

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth>
                                        <InputLabel id="report-dataset-label">O que você quer ver</InputLabel>
                                        <Select
                                            labelId="report-dataset-label"
                                            value={datasetId}
                                            label="O que você quer ver"
                                            onChange={event => handleTrocarDataset(event.target.value)}
                                        >
                                            {datasets.map(item => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {dataset?.descricao && (
                                        <Typography variant="body2" color="text.secondary" align="left" sx={{ mt: 1 }}>
                                            {dataset.descricao}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={6} md={2}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label="De"
                                        value={inicio}
                                        onChange={event => setInicio(event.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={6} md={2}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label="Até"
                                        value={fim}
                                        onChange={event => setFim(event.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    {temAnalyticsPro ? (
                                        <MultipleSelectUnidade
                                            label="Lojas"
                                            unidadeList={lojas}
                                            unidade={chavesDosIds(unidades, lojas)}
                                            handleChangeTo={chaves => setUnidades(idsDasChaves(chaves))}
                                            renderValue={(selecionadas, lista) => (
                                                rotuloSelecao(selecionadas?.length || 0, lista.length)
                                            )}
                                        />
                                    ) : (
                                        <MultipleSelectUnidadeBasic label="Lojas" />
                                    )}

                                    <Typography
                                        variant="body2"
                                        color={faltaLoja ? 'error' : 'text.secondary'}
                                        align="left"
                                        sx={{ mt: 1 }}
                                    >
                                        {faltaLoja ? 'Escolha pelo menos uma loja.' : AJUDA_LOJAS}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 3 }} />

                            <Box>
                                <TituloDoPasso numero={2} titulo="Como mostrar" />

                                {podeAgrupar(dataset) && (
                                    <>
                                        <RadioGroup
                                            row
                                            value={modoAgrupado ? 'agrupado' : 'linhas'}
                                            onChange={event => handleTrocarModo(event.target.value === 'agrupado')}
                                        >
                                            <FormControlLabel
                                                value="linhas"
                                                control={<Radio />}
                                                label="Lista completa"
                                            />
                                            <FormControlLabel
                                                value="agrupado"
                                                control={<Radio />}
                                                label="Totais"
                                            />
                                        </RadioGroup>

                                        <Typography variant="body2" color="text.secondary" align="left">
                                            Totais junta tudo da mesma loja, dia ou cliente numa linha só,
                                            com a soma, e mostra um gráfico.
                                        </Typography>
                                    </>
                                )}

                                {!!dataset && !podeAgrupar(dataset) && (
                                    <Typography variant="body2" color="text.secondary" align="left">
                                        Este relatório já vem somado.
                                    </Typography>
                                )}

                                {modoAgrupado && (
                                    <Grid container spacing={2} sx={{ mt: 0 }} alignItems="flex-start">
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth error={!metricas.length}>
                                                <InputLabel id="report-somar-label">Somar</InputLabel>
                                                <Select
                                                    multiple
                                                    labelId="report-somar-label"
                                                    value={nomesDasContas}
                                                    onChange={event => handleTrocarMetricas(event.target.value)}
                                                    input={<OutlinedInput label="Somar" />}
                                                    renderValue={nomes => nomes
                                                        .map(nome => rotuloNoSeletor(
                                                            opcoesDeConta.find(opcao => opcao.nome === nome),
                                                            dataset,
                                                        ))
                                                        .join(', ')}
                                                >
                                                    <ListSubheader>
                                                        {metricas.length >= MAX_METRICAS
                                                            ? `Até ${MAX_METRICAS} contas. Desmarque uma para trocar.`
                                                            : `Até ${MAX_METRICAS} contas. A primeira vira o gráfico.`}
                                                    </ListSubheader>
                                                    {opcoesDeConta.map(opcao => (
                                                        <MenuItem
                                                            key={opcao.nome}
                                                            value={opcao.nome}
                                                            disabled={
                                                                metricas.length >= MAX_METRICAS
                                                                && !temMetrica(metricas, opcao.nome)
                                                            }
                                                        >
                                                            <Checkbox checked={temMetrica(metricas, opcao.nome)} />
                                                            <ListItemText primary={rotuloNoSeletor(opcao, dataset)} />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <Typography
                                                variant="body2"
                                                color={metricas.length ? 'text.secondary' : 'error'}
                                                align="left"
                                                sx={{ mt: 1 }}
                                            >
                                                {metricas.length
                                                    ? 'A primeira que você marcar vira o gráfico.'
                                                    : 'Escolha pelo menos uma conta.'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth error={faltaSomarPor}>
                                                <InputLabel id="report-agrupar-label">por</InputLabel>
                                                <Select
                                                    multiple
                                                    labelId="report-agrupar-label"
                                                    value={agruparPor}
                                                    onChange={event => handleTrocarAgruparPor(event.target.value)}
                                                    input={<OutlinedInput label="por" />}
                                                    renderValue={selecionadas => selecionadas.map(rotuloColuna).join(', ')}
                                                >
                                                    <ListSubheader>
                                                        {textoDoTeto(agruparPor.length, MAX_COLUNAS_AGRUPAMENTO)}
                                                    </ListSubheader>
                                                    {colunasParaAgrupar(dataset).map(coluna => (
                                                        <MenuItem
                                                            key={coluna.nome}
                                                            value={coluna.nome}
                                                            disabled={
                                                                agruparPor.length >= MAX_COLUNAS_AGRUPAMENTO
                                                                && !agruparPor.includes(coluna.nome)
                                                            }
                                                        >
                                                            <Checkbox checked={agruparPor.includes(coluna.nome)} />
                                                            <ListItemText primary={coluna.label} />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <Typography
                                                variant="body2"
                                                color={faltaSomarPor ? 'error' : 'text.secondary'}
                                                align="left"
                                                sx={{ mt: 1 }}
                                            >
                                                {faltaSomarPor && 'Escolha por onde somar. '}
                                                {dicaSomarPor(dataset)}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Mostrar só os maiores"
                                                value={limiteTexto}
                                                onChange={event => handleTrocarLimite(event.target.value)}
                                                error={!limiteValidado.ok}
                                                helperText={limiteValidado.ok
                                                    ? 'Ex.: 10 mostra os 10 maiores. Em branco mostra todos.'
                                                    : limiteValidado.erro}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Box>

                            {!modoAgrupado && (
                                <Box sx={{ mt: 2 }}>
                                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                                        <Typography variant="subtitle2" align="left">
                                            Colunas que vão aparecer
                                        </Typography>
                                        <Button size="small" onClick={handleMarcarTodasColunas}>
                                            Marcar todas
                                        </Button>
                                        <Button size="small" onClick={handleLimparColunas}>
                                            Limpar
                                        </Button>
                                    </Box>

                                    <FormGroup row sx={{ mt: 1 }}>
                                        {(dataset?.colunas || []).map(coluna => (
                                            <FormControlLabel
                                                key={coluna.nome}
                                                sx={{ width: { xs: '100%', sm: '48%', md: '31%' } }}
                                                control={(
                                                    <Checkbox
                                                        checked={colunas.includes(coluna.nome)}
                                                        onChange={() => handleAlternarColuna(coluna.nome)}
                                                    />
                                                )}
                                                label={coluna.label}
                                            />
                                        ))}
                                    </FormGroup>

                                    {!colunas.length && (
                                        <Typography variant="body2" color="error" align="left">
                                            Marque pelo menos uma coluna para gerar o relatório.
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {(!!ordenacao || !!filtros.length || limiteNoResumo) && (
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
                                    <Typography variant="body2" color="text.secondary" align="left">
                                        {!!ordenacao && (
                                            `Ordenado por ${rotuloColuna(ordenacao.coluna)} (${ordenacao.direcao === 'desc' ? 'do maior para o menor' : 'do menor para o maior'}).`
                                        )}
                                        {!!filtros.length && (
                                            ` Mostrando só: ${filtros.map(filtro => (
                                                `${rotuloColuna(filtro.coluna)} ${TEXTO_OPERADOR[filtro.operador]} ${filtro.valor}`
                                            )).join('; ')}.`
                                        )}
                                        {limiteNoResumo && ` Mostrando só os ${limite} primeiros.`}
                                    </Typography>

                                    <Button size="small" onClick={handleLimparOrdenacaoEFiltros}>
                                        Limpar ordem e filtros
                                    </Button>
                                </Box>
                            )}

                            <Divider sx={{ my: 3 }} />

                            <TituloDoPasso
                                numero={3}
                                titulo="Gerar"
                                ajuda="O relatório aparece aqui embaixo. Depois dá para baixar em CSV ou salvar a imagem do gráfico."
                            />

                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                <LoadingButton
                                    variant="contained"
                                    size="large"
                                    startIcon={<SearchIcon />}
                                    loading={gerando}
                                    loadingIndicator="Gerando..."
                                    disabled={!podeGerar}
                                    onClick={handleGerar}
                                >
                                    Gerar relatório
                                </LoadingButton>

                                <Button
                                    variant="outlined"
                                    size="large"
                                    startIcon={<DownloadIcon />}
                                    disabled={!resultado?.linhas?.length}
                                    onClick={handleExportar}
                                >
                                    Exportar CSV
                                </Button>

                                {!!grafico && (
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        startIcon={<ImageIcon />}
                                        onClick={handleBaixarGrafico}
                                    >
                                        Baixar gráfico (imagem)
                                    </Button>
                                )}
                            </Box>

                            {!!erro && (
                                <Alert severity="error" sx={{ mt: 3 }}>
                                    {erro}
                                </Alert>
                            )}

                            {!!resultado && (
                                <Box sx={{ mt: 3 }}>
                                    <Divider sx={{ mb: 3 }} />

                                    <Typography variant="subtitle1" component="h3" align="left" sx={{ mb: 2 }}>
                                        Resultado
                                    </Typography>

                                    {resultado.truncado && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            Período muito grande — reduza o período ou as lojas.
                                            O relatório traz só as primeiras {resultado.linhas.length.toLocaleString('pt-BR')} linhas.
                                        </Alert>
                                    )}

                                    {!!resultado.unidadesIndisponiveis?.length && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            Estas lojas não responderam a tempo e ficaram de fora:{' '}
                                            {resultado.unidadesIndisponiveis.join(', ')}. Tente de novo com um período menor.
                                        </Alert>
                                    )}

                                    {resultado.linhas.length ? (
                                        <>
                                            <GraficoRelatorio grafico={grafico} containerRef={graficoRef} />

                                            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                                <Chip
                                                    size="small"
                                                    color="primary"
                                                    label={`${resultado.total.toLocaleString('pt-BR')} linha(s)`}
                                                />
                                                <Typography variant="body2" color="text.secondary" align="left">
                                                    {legendaDoResultado()}
                                                </Typography>
                                            </Box>

                                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
                                                <Table size="small" stickyHeader>
                                                    <TableHead>
                                                        <TableRow>
                                                            {resultado.colunas.map(coluna => (
                                                                <TableCell key={coluna.nome}>{coluna.label}</TableCell>
                                                            ))}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {linhasNaTela.map((linha, indice) => (
                                                            <TableRow key={indice} hover>
                                                                {resultado.colunas.map(coluna => (
                                                                    <TableCell key={coluna.nome}>
                                                                        {colunasAgrupadas.includes(coluna.nome)
                                                                            ? formatarCelulaDoGrupo(linha[coluna.nome], coluna)
                                                                            : formatarCelula(linha[coluna.nome], coluna.tipo)}
                                                                    </TableCell>
                                                                ))}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </>
                                    ) : (
                                        <Alert severity="info">
                                            Nenhum resultado nesse período. Tente outras datas ou outra loja.
                                        </Alert>
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </>
    )
}
